import { PublicClientApplication } from "@azure/msal-browser";

// MSAL 配置
//https://learn.microsoft.com/zh-tw/entra/identity-platform/msal-js-initializing-client-applications
const msalConfig = {
  auth: {
    clientId: process.env.SSO_ID || "", // 從環境變數獲取 Client ID
    authority: `https://login.microsoftonline.com/${process.env.AAD_TENANT_ID || "common"}`,
    redirectUri: window.location.origin + window.location.pathname,
  },
  cache: {
    cacheLocation: "sessionStorage", // 使用 sessionStorage 儲存 token
    storeAuthStateInCookie: false,
  },
};

// 創建 MSAL 實例
let msalInstance = null;

export const getMsalInstance = () => {
  if (!msalInstance && msalConfig.auth.clientId) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
};

/**
 * 獲取登入所需的權限範圍
 * 使用 Graph API 作為 audience，後端會直接使用這個 token（不經過 OBO）
 */
const getLoginRequest = () => {
  // 直接請求 Graph API 的權限
  // 後端會檢查 token 的 audience，如果是 Graph API 就直接使用
  return {
    scopes: [
      "https://graph.microsoft.com/User.Read",
      "https://graph.microsoft.com/Group.Read.All",
      "https://graph.microsoft.com/Directory.Read.All",
      "profile",
      "openid",
    ],
  };
};

/**
 * 使用互動式登入獲取 token
 * @returns {Promise<string|null>} 訪問令牌或 null
 */
export const loginWithMsal = async () => {
  try {
    const msal = getMsalInstance();
    if (!msal) {
      console.error("MSAL 未初始化，請檢查 CLIENT_ID 配置");
      return null;
    }

    // 初始化 MSAL
    await msal.initialize();

    // 獲取登入請求配置
    const loginRequest = getLoginRequest();
    
    //  returns all the accounts currently in the cache.
    const accounts = msal.getAllAccounts();
    if (accounts.length > 0) {
      // 如果有已登入的帳戶，嘗試靜默獲取 token
      try {
        const silentResult = await msal.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        if (silentResult && silentResult.accessToken) {
          return silentResult.accessToken;
        }
      } catch (silentError) {
        console.log("靜默獲取 token 失敗，將使用互動式登入", silentError);
      }
    }

    // 使用互動式登入
    const loginResult = await msal.loginPopup(loginRequest);
    if (loginResult && loginResult.accessToken) {
      return loginResult.accessToken;
    }

    return null;
  } catch (error) {
    console.error("MSAL 登入錯誤:", error);
    return null;
  }
};

/**
 * 登出
 */
export const logoutMsal = async () => {
  try {
    const msal = getMsalInstance();
    if (msal) {
      const accounts = msal.getAllAccounts();
      if (accounts.length > 0) {
        await msal.logoutPopup({
          account: accounts[0],
        });
      }
    }
  } catch (error) {
    console.error("MSAL 登出錯誤:", error);
  }
};

/**
 * 獲取當前登入的帳戶
 * @returns {Object|null} 帳戶資訊或 null
 */
export const getCurrentAccount = () => {
  try {
    const msal = getMsalInstance();
    if (msal) {
      const accounts = msal.getAllAccounts();
      return accounts.length > 0 ? accounts[0] : null;
    }
    return null;
  } catch (error) {
    console.error("獲取帳戶資訊錯誤:", error);
    return null;
  }
};

/**
 * 檢查 token 是否過期
 * @param {string} token - JWT token
 * @returns {boolean} - true 表示過期或無效
 */
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const base64Url = token.split('.')[1];
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    while (base64.length % 4) {
      base64 += '=';
    }

    const payload = JSON.parse(atob(base64));
    const exp = payload.exp;
    const now = Math.floor(Date.now() / 1000);

    return exp <= (now + 300); // 提前 5 分鐘視為過期
  } catch (error) {
    console.error("Error parsing token:", error);
    return true;
  }
};

